import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Card,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Spinner,
  EmptyState,
  Icon,
} from "@shopify/polaris";
import { PlusIcon } from "@shopify/polaris-icons";
import { AppDispatch, RootState } from "configureStore";
import { setSelectedReservation } from "../reservations.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { Reservation, ReservationStatus } from "services/reservations.service";
import ReservationCard from "./ReservationCard";
import DataTable from "components/datatable/Datatable";

interface ReservationsSectionProps {
  orderId: string;
  companyId: string;
}

export const getReservationStatusBadgeTone = (
  status: ReservationStatus
): "info" | "success" | "warning" | "critical" | "attention" => {
  switch (status) {
    case "draft":
      return "info";
    case "confirmed":
      return "success";
    case "cancelled":
      return "critical";
    default:
      return "info";
  }
};

export default function ReservationsSection({ orderId, companyId }: ReservationsSectionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  // Get order entity with reservations from order container slice
  const { orderEntity, isLoading } = useSelector(
    (state: RootState) => state.orderContainerReducer
  );

  const reservations = (orderEntity?.reservations || []) as Reservation[];
  const orderItems = orderEntity?.items || [];

  const refreshReservations = () => {
    if (companyId && orderId) {
      dispatch(fetchOrderDetails({ companyId, orderId }));
    }
  };

  // Calculate reservation summary with fulfillment status: Reserved | Delivered | Remaining
  const reservationSummary = useMemo(() => {
    const summary: Record<string, { 
      productId: string;
      productTitle: string;
      sku: string;
      ordered: number;
      reserved: number;
      delivered: number;
    }> = {};

    // Initialize with order items (sum quantities if same product appears multiple times)
    orderItems.forEach((item: any) => {
      const productRef = item.product;
      const key = typeof productRef === "object" && productRef !== null 
        ? productRef.id 
        : (productRef || item.id);
      
      if (!key) return;
      
      if (summary[key]) {
        summary[key].ordered += item.quantity || 0;
      } else {
        summary[key] = {
          productId: key,
          productTitle: item.productTitle || (typeof productRef === "object" ? productRef?.title : null) || "-",
          sku: item.sku || (typeof productRef === "object" ? productRef?.sku : null) || "-",
          ordered: item.quantity || 0,
          reserved: 0,
          delivered: 0,
        };
      }
    });

    // Sum reserved and fulfilled quantities from confirmed reservations
    (reservations || []).forEach((reservation) => {
      if (reservation.status === "confirmed") {
        (reservation.items || []).forEach((item: any) => {
          const product = typeof item.product === "object" ? item.product : null;
          const productId = product?.id || (typeof item.product === "string" ? item.product : "");
          const fulfilledQty = item.fulfilledQuantity || 0;
          const remainingReserved = Math.max(0, (item.quantity || 0) - fulfilledQty);
          
          if (summary[productId]) {
            summary[productId].reserved += remainingReserved;
            summary[productId].delivered += fulfilledQty;
          } else {
            summary[productId] = {
              productId,
              productTitle: (product as any)?.title || "-",
              sku: (product as any)?.sku || "-",
              ordered: 0,
              reserved: remainingReserved,
              delivered: fulfilledQty,
            };
          }
        });
      }
    });

    return Object.values(summary);
  }, [orderItems, reservations]);

  const handleCreateReservation = () => {
    dispatch(setModalxComponent("ReservationForm"));
    dispatch(setModalxComponentProps({ orderId, mode: "create" }));
  };

  const handleEditReservation = (reservation: Reservation) => {
    dispatch(setSelectedReservation(reservation));
    dispatch(setModalxComponent("ReservationForm"));
    dispatch(
      setModalxComponentProps({ orderId, reservationId: reservation.id, mode: "edit" })
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <Spinner size="large" />
        </div>
      </Card>
    );
  }

  // Prepare summary for DataTable with remaining column (remaining to deliver from reservations)
  const summaryCollection = reservationSummary.map((item) => {
    // Remaining to reserve from order
    const totalReserved = item.reserved + item.delivered;
    const remainingToReserve = Math.round((item.ordered - totalReserved) * 100) / 100;
    // Remaining to deliver from reservations
    const remainingToDeliver = Math.round(item.reserved * 100) / 100;
    return {
      ...item,
      reserved: Math.round(item.reserved * 100) / 100,
      delivered: Math.round(item.delivered * 100) / 100,
      remainingToReserve,
      remainingToDeliver,
      tone: remainingToDeliver <= 0 ? "success" : "attention",
    };
  });

  return (
    <BlockStack gap="400">
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingSm">
            {t("reservations")}
          </Text>
          <Button onClick={handleCreateReservation}>
            <Icon source={PlusIcon as any} />
            {t("reservations_create")}
          </Button>
        </InlineStack>
      </Card>

      {/* Reservation Summary */}
      {orderItems.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("reservations_summary")}
            </Text>
            <DataTable
              collection={summaryCollection}
              columns={[
                { key: "sku", name: t("sku") },
                { key: "productTitle", name: t("product_name") },
                { key: "ordered", name: t("ordered"), type: "number" },
                { key: "reserved", name: t("reserved"), type: "number" },
                { key: "delivered", name: t("delivered"), type: "number" },
                { key: "remainingToDeliver", name: t("remaining_to_deliver"), type: "badge" },
              ]}
              isTotalsDisabled
            />
          </BlockStack>
        </Card>
      )}

      {!reservations || reservations.length === 0 ? (
        <Card>
          <EmptyState
            heading={t("reservations_no_reservations")}
            image=""
            action={{
              content: t("reservations_create_first"),
              onAction: handleCreateReservation,
            }}
          >
            <p>{t("reservations_no_reservations_description")}</p>
          </EmptyState>
        </Card>
      ) : (
        <BlockStack gap="300">
          {reservations.filter((r) => r && r.id).map((reservation) => (
            <ReservationCard
              key={reservation.id}
              reservation={reservation}
              onEdit={() => handleEditReservation(reservation)}
              onRefresh={refreshReservations}
            />
          ))}
        </BlockStack>
      )}
    </BlockStack>
  );
}
